import React, { useState } from 'react';
import { usePermissions } from 'react-admin';
import { Box, Paper, Typography, Chip, Stack, LinearProgress, Button, Alert } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { PERMISSIONS, DATE_FORMATER } from '../../../constants';
import { campaignSituationLabel, campaignSituationColor, NOTIFICATION_STATUS_COLOR, notificationStatusLabel } from './labels';
import AdvanceDialog from './AdvanceDialog';
import CancelDialog from './CancelDialog';
import CampaignAiInsight from './CampaignAiInsight';
import CampaignSetup from './CampaignSetup';

interface CampaignHeaderProps {
  campaign: any;
}

const formatDateTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', DATE_FORMATER as any) : null);

const CampaignHeader: React.FC<CampaignHeaderProps> = ({ campaign }) => {
  const { permissions } = usePermissions();
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const canAct = !!permissions?.includes(PERMISSIONS.CUSTOMER_RECOVERY_RULES) && campaign.status === 'running';
  const isLastLevel = campaign.currentLevelOrder >= campaign.levelsCount;
  const dayNumber = campaign.progress?.dayNumber;
  const totalDays = campaign.progress?.totalDays;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
      {campaign.legacy && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Regra do modelo antigo, encerrada na migração para campanhas.
        </Alert>
      )}

      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight={700}>{campaign.name}</Typography>
        <Chip label={campaignSituationLabel(campaign)} color={campaignSituationColor(campaign) as any} />
        {campaign.currentLevelNotification && (
          <Chip
            size="small"
            variant="outlined"
            icon={<NotificationsActiveIcon fontSize="small" />}
            label={notificationStatusLabel(campaign.currentLevelNotification.status, campaign.currentLevelNotification.event?.sendAt)}
            color={NOTIFICATION_STATUS_COLOR[campaign.currentLevelNotification.status as keyof typeof NOTIFICATION_STATUS_COLOR] as any}
          />
        )}
      </Stack>

      {campaign.description && (
        <Typography variant="body2" color="text.secondary" mt={0.5}>{campaign.description}</Typography>
      )}

      {campaign.status === 'running' && dayNumber != null && totalDays != null && (
        <Box mt={2} maxWidth={420}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">Dia {dayNumber} de {totalDays}</Typography>
            <Typography variant="caption" color="text.secondary">{campaign.currentLevelName}</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={Math.min(100, (dayNumber / totalDays) * 100)} />
        </Box>
      )}

      <Stack direction="row" spacing={3} mt={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          Criada por {campaign.createdBy?.name || 'Sistema'} em {formatDateTime(campaign.createdAt)}
        </Typography>
        {campaign.finishedAt && (
          <Typography variant="body2" color="text.secondary">
            {campaign.status === 'cancelled' ? 'Encerrada' : 'Finalizada'} por {campaign.finishedBy?.name || 'Sistema'} em {formatDateTime(campaign.finishedAt)}
            {campaign.cancelReason ? ` — ${campaign.cancelReason}` : ''}
          </Typography>
        )}
      </Stack>

      <CampaignSetup campaign={campaign} />

      <CampaignAiInsight campaignId={campaign.id} />

      {canAct && (
        <Stack direction="row" spacing={2} mt={2}>
          <Button
            variant="contained"
            startIcon={isLastLevel ? <StopCircleIcon /> : <ArrowForwardIcon />}
            onClick={() => setAdvanceOpen(true)}
          >
            {isLastLevel ? 'Finalizar agora' : `Avançar para o nível ${campaign.currentLevelOrder + 1}`}
          </Button>
          <Button variant="outlined" color="error" onClick={() => setCancelOpen(true)}>
            Encerrar campanha
          </Button>
        </Stack>
      )}

      <AdvanceDialog open={advanceOpen} onClose={() => setAdvanceOpen(false)} campaign={campaign} />
      <CancelDialog open={cancelOpen} onClose={() => setCancelOpen(false)} campaign={campaign} />
    </Paper>
  );
};

export default CampaignHeader;
